/**
 * @engine/vfx - Compiler Types
 * 
 * Type definitions for the VFX compilation pipeline.
 */

import type { VFXNodeInstance, VFXContextDefinition, ParticleAttributes } from '../types';
import type { VFXTweakBinding } from './codegen/Tweaks';

// ============================================================================
// COMPILATION RESULT
// ============================================================================

/**
 * A compiled VFX system (one per Spawn context).
 */
export interface VFXCompiledSystem {
  /** Unique system ID (from Spawn context) */
  readonly id: string;
  
  /** Display name */
  readonly name: string;
  
  /** Maximum particles */
  readonly maxParticles: number;
  
  // Spawn Configuration
  readonly spawn: VFXSpawnConfig;
  
  // Shaders
  readonly initShader: string;
  readonly updateShader: string;
  readonly vertexShader: string;
  readonly fragmentShader: string;
  
  // Event links
  readonly inputEvent?: VFXEventLink;
  readonly outputEvent?: VFXEventLink;
  
  // Texture binding
  readonly usesTexture?: boolean;
  readonly textureSlot?: number;
  
  // Light emission (vfx.output.light)
  readonly emitsLight?: boolean;
  /** Whether the light system also renders a visible quad */
  readonly lightRenderQuad?: boolean;
  /** Whether this system receives Forward+ lighting (vfx.output.lit_quad) */
  readonly isLit?: boolean;

  /**
   * Editor live-preview tweak slots (only when compiled with
   * `tweakableInputs`). Presence ⇒ the shaders read a `tweaks[]` array
   * appended to the variables buffer; the renderer must size the buffer to
   * TWEAK_BUFFER_SIZE and edits write slots instead of recompiling.
   */
  readonly tweaks?: readonly VFXTweakBinding[];
  readonly lightConfig?: {
    readonly radius: number;
    readonly intensity: number;
    readonly falloff: number;
    readonly colorMode: number;
    readonly overrideColor: readonly number[];
    readonly intensityFromAlpha: boolean;
  };
}

/**
 * Spawn configuration extracted from Spawn context.
 * Models Unity VFX Graph spawn behavior:
 * - rate: Constant rate (spawnCount += rate * dt)
 * - variable_rate: Interpolate between rateMin and rateMax over period
 * - single_burst: Spawn count particles once after delay
 * - periodic_burst: Spawn count particles every period seconds (infinite repeat)
 * - burst: Spawn count particles, repeat times, with delay then interval
 * - event: Spawn on GPU event (particle death, collision)
 */
export interface VFXSpawnConfig {
  /** Spawn mode */
  readonly mode: 'rate' | 'variable_rate' | 'burst' | 'single_burst' | 'periodic_burst' | 'event' | 'none';
  
  /** Spawn rate (particles/sec) for rate mode */
  readonly rate?: number;
  
  /** Variable rate configuration */
  readonly variableRate?: {
    /** Minimum rate (particles/sec) */
    rateMin: number;
    /** Maximum rate (particles/sec) */
    rateMax: number;
  };
  
  /** Burst configuration */
  readonly burst?: {
    /** Number of particles to spawn per burst */
    count: number;
    /** Initial delay before first burst (seconds) */
    delay: number;
    /** Number of times to repeat (-1 for infinite) */
    repeat: number;
    /** Interval between bursts (seconds) */
    interval: number;
  };
  
  /** Event ID for event-driven spawn */
  readonly eventId?: number;
  
  /** Spawn ratio multiplier for event mode */
  readonly eventRatio?: number;
}

/**
 * GPU Event link between systems.
 */
export interface VFXEventLink {
  /** Event ID (matches source output to target input) */
  readonly eventId: number;
  
  /** Spawn count per event (for output events) */
  readonly spawnCount?: number;
}

/**
 * Full compilation result for a VFX graph.
 */
export interface VFXCompilationResult {
  /** All compiled systems */
  readonly systems: readonly VFXCompiledSystem[];
  
  /** Shared particle struct definition */
  readonly particleStruct: string;
  
  /** Validation errors (if any) */
  readonly errors: readonly VFXCompilationError[];
  
  /** Validation warnings */
  readonly warnings: readonly string[];
  
  /** Whether compilation was successful */
  readonly success: boolean;
}

/**
 * Compilation error.
 */
export interface VFXCompilationError {
  /** Error code */
  readonly code: VFXErrorCode;

  /** Human-readable message */
  readonly message: string;

  /** Related context ID */
  readonly contextId?: string;

  /** Related node ID */
  readonly nodeId?: string;

  /**
   * Graph nodes a shader diagnostic points at (SHADER_ERROR only; resolved
   * via the generated stage's source map). Editors highlight these.
   */
  readonly nodeIds?: readonly string[];
}

/**
 * Error codes for validation.
 */
export type VFXErrorCode =
  | 'NO_SPAWN'           // No Spawn context or empty
  | 'NO_INITIALIZE'      // Initialize context empty
  | 'NO_UPDATE'          // Update context missing
  | 'NO_OUTPUT'          // Output context empty
  | 'INVALID_SPAWN'      // Spawn without rate/burst config
  | 'INVALID_CAPACITY'   // Particle count exceeds WebGPU limit
  | 'ORPHAN_NODE'        // Node not in any context
  | 'TYPE_MISMATCH'      // Port type incompatibility
  | 'INVALID_NODE'       // Unknown node type
  | 'EVENT_LOOP'         // Circular event dependency
  | 'MULTI_SPAWN_NODE'   // Multiple spawn nodes in one context
  | 'DISCONNECTED_FLOW'  // Context not connected in flow
  | 'SHADER_ERROR';      // WGSL validation error

// Re-export from main types
export type { VFXEventTriggerMode, VFXEventTriggerConfig } from '../types';
import type { VFXEventTriggerConfig } from '../types';

// ============================================================================
// EXTRACTED SYSTEM
// ============================================================================

/**
 * Intermediate representation of a VFX system before shader generation.
 * One per Spawn context.
 */
export interface VFXExtractedSystem {
  /** System ID */
  readonly id: string;
  
  /** Display name */
  readonly name: string;
  
  /** Maximum particles (from context or default) */
  readonly maxParticles: number;
  
  /** Spawn context info */
  readonly spawn: {
    contextId: string;
    nodes: readonly VFXNodeInstance[];
    config: VFXSpawnConfig;
  };
  
  /** Initialize context info */
  readonly initialize: {
    contextId: string;
    nodes: readonly VFXNodeInstance[];
  };
  
  /** Update context info */
  readonly update: {
    contextId: string;
    nodes: readonly VFXNodeInstance[];
    /** Event trigger configuration */
    trigger?: VFXEventTriggerConfig;
  };
  
  /** Output context info */
  readonly output: {
    contextId: string;
    nodes: readonly VFXNodeInstance[];
  };
  
  /** Optional GPU Event context */
  readonly event?: {
    contextId: string;
    nodes: readonly VFXNodeInstance[];
  };
  
  /** Input event (if spawned by GPU event) */
  readonly inputEvent?: VFXEventLink;
  
  /** Output event (if triggers GPU event on death) */
  readonly outputEvent?: VFXEventLink;
}

// ============================================================================
// COMPILER OPTIONS
// ============================================================================

/**
 * Options for VFX compilation.
 */
export interface VFXCompilerOptions {
  /** Custom particle attributes */
  readonly attributes?: ParticleAttributes;

  /** Workgroup size for compute shaders */
  readonly workgroupSize?: number;

  /** Default max particles per system */
  readonly defaultMaxParticles?: number;

  /** Enable debug comments in shaders */
  readonly debug?: boolean;

  /** Strict validation (errors on warnings) */
  readonly strict?: boolean;

  /**
   * Editor live-preview: route unconnected float-family inputs through
   * uniform tweak slots (compiled systems carry the slot map; edits write the
   * buffer instead of recompiling). NEVER set for import/build compiles — the
   * cached/player shaders must stay literal-baked. See codegen/Tweaks.ts.
   */
  readonly tweakableInputs?: boolean;
}

/**
 * Default compiler options.
 */
export const DEFAULT_COMPILER_OPTIONS: Required<VFXCompilerOptions> = {
  attributes: undefined as any, // Will use DEFAULT_PARTICLE_ATTRIBUTES
  workgroupSize: 64,
  defaultMaxParticles: 10000,
  debug: false,
  strict: false,
  tweakableInputs: false,
};
